<template>
  <router-view />
  <Toaster
    :theme="theme.current"
    :container-aria-label="$t('interface.notifications')"
    :toast-options="{ closeButtonAriaLabel: $t('common.close') }"
    rich-colors
    position="bottom-right"
  />
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Toaster } from 'vue-sonner'

import { useThemeStore } from '@/app/themeStore'
import { useAuthStore } from '@/features/auth/authStore'
import { useImpersonationStore } from '@/features/auth/impersonationStore'
import { clientConfig } from '@/app/branding'

const auth = useAuthStore()
const impersonation = useImpersonationStore()
const theme = useThemeStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
watchEffect(() => {
  document.title = route.meta.title ? `${t(route.meta.title)} · ${clientConfig.name}` : clientConfig.name
})

theme.boot()
auth.boot()
impersonation.boot()

function stripImpersonationPayload() {
  const url = new URL(window.location.href)
  url.searchParams.delete('data')
  if (url.hash.includes('?')) url.hash = url.hash.split('?')[0] ?? ''
  window.history.replaceState(window.history.state, '', url)
}

async function initializeImpersonation() {
  let payload = typeof route.query.data === 'string' ? route.query.data : null
  if (!payload) payload = window.location.hash.match(/[?&]data=([^&]+)/)?.[1] ?? null
  if (!payload) return false
  stripImpersonationPayload()
  try {
    const profile = await impersonation.start(payload)
    await router.replace({ name: profile.is_marketer ? 'marketer' : 'home' })
    return true
  } catch {
    impersonation.$reset()
    await router.replace({ name: 'login' })
    return false
  }
}

async function restoreProfile() {
  if (!auth.accessToken || auth.profileSynchronized) return
  try {
    await auth.fetchProfile()
  } catch {
    auth.logout()
  }
}

function handleAuthExpired() {
  auth.logout()
  void router.push({ name: 'login', query: { redirect: route.name?.toString() } })
}

onMounted(async () => {
  const impersonated = await initializeImpersonation()
  if (!impersonated) await restoreProfile()
  window.addEventListener('auth:expired', handleAuthExpired)
})

onBeforeUnmount(() => window.removeEventListener('auth:expired', handleAuthExpired))
</script>
